import {
  authHeader,
  config,
  options,
  handleResponse,
  handleError,
} from "../common/helpers";

const SOP_API_BASE = config.sopApiUrl;
const getSopUrl = (path) => options(`${SOP_API_BASE}${path}`);

export const checkSopAccess = async (email, token) => {
  const url = getSopUrl(`/admin/whitelist/check/${encodeURIComponent(email)}`);
  const response = await fetch(url, {
    method: "GET",
    headers: { Authenticate: token },
  });
  return handleResponse(response, handleError);
};

const getAuthHeaders = () => {
  const headers = authHeader();
  return {
    "Content-Type": "application/json",
    Authenticate: headers.Authenticate,
  };
};

export const uploadDocument = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  const authHeaders = authHeader();
  const url = getSopUrl("/upload");

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authenticate: authHeaders.Authenticate,
    },
    body: formData,
  });

  return handleResponse(response, handleError);
};

export const getProcessingUploads = async () => {
  const response = await fetch(getSopUrl("/uploads/processing"), {
    method: "GET",
    headers: getAuthHeaders(),
  });

  return handleResponse(response, handleError);
};

export const getParts = async () => {
  const response = await fetch(getSopUrl("/parts"), {
    method: "GET",
    headers: getAuthHeaders(),
  });

  return handleResponse(response, handleError);
};

export const getProcessingStatus = async (requestId) => {
  const response = await fetch(getSopUrl(`/status/${requestId}`), {
    method: "GET",
    headers: getAuthHeaders(),
  });

  return handleResponse(response, handleError);
};

export const deleteProcessingUpload = async (requestId) => {
  const response = await fetch(getSopUrl(`/upload/${requestId}`), {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  return handleResponse(response, handleError);
};

export const getPart = async (partId) => {
  const response = await fetch(getSopUrl(`/parts/${partId}`), {
    method: "GET",
    headers: getAuthHeaders(),
  });

  return handleResponse(response, handleError);
};

export const getPartProcedures = async (partId) => {
  const response = await fetch(getSopUrl(`/parts/${partId}/procedures`), {
    method: "GET",
    headers: getAuthHeaders(),
  });

  return handleResponse(response, handleError);
};

export const getAssets = async () => {
  const response = await fetch(getSopUrl("/assets"), {
    method: "GET",
    headers: getAuthHeaders(),
  });

  return handleResponse(response, handleError);
};

export const getOrgContainers = async (orgId) => {
  const requestOptions = {
    method: "GET",
    headers: authHeader(),
  };
  const url = options(
    config.apiUrl +
      "/organization/" +
      orgId +
      "/container?as_service=true&count=0",
  );
  const response = await fetch(url, requestOptions);
  return handleResponse(response, handleError);
};

export const getContainerAssets = async (orgId, containerId) => {
  const requestOptions = {
    method: "GET",
    headers: authHeader(),
  };
  const url = options(
    config.apiUrl +
      "/asset?orgId=" +
      orgId +
      "&filter=container.containerId=" +
      containerId +
      "&count=10&offset=0&orderBy=adminDesc&orderDir=asc&",
  );
  const response = await fetch(url, requestOptions);
  return handleResponse(response, handleError);
};

export const linkAssetsToPart = async (partId, assetIds) => {
  const response = await fetch(getSopUrl(`/parts/${partId}/link-assets`), {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ asset_ids: assetIds }),
  });

  return handleResponse(response, handleError);
};

export const approvePart = async (partId, selectedIndices, make, model) => {
  const response = await fetch(getSopUrl(`/reviews/${partId}/approve`), {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      selected_procedure_indices: selectedIndices,
      make,
      model,
    }),
  });

  return handleResponse(response, handleError);
};

export const updatePartMetadata = async (partId, updates) => {
  const response = await fetch(getSopUrl(`/parts/${partId}`), {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify(updates),
  });

  return handleResponse(response, handleError);
};

export const getLinkedAssets = async (partId) => {
  const response = await fetch(getSopUrl(`/parts/${partId}/linked-assets`), {
    method: "GET",
    headers: getAuthHeaders(),
  });

  return handleResponse(response, handleError);
};

export const unlinkAssetFromPart = async (partId, assetId) => {
  const response = await fetch(
    getSopUrl(`/parts/${partId}/unlink-asset/${assetId}`),
    {
      method: "DELETE",
      headers: getAuthHeaders(),
    },
  );

  return handleResponse(response, handleError);
};

export const pollProcessingStatus = async (
  requestId,
  onStatusUpdate = () => {},
  maxAttempts = 120,
  interval = 3000,
  shouldCancel = () => false,
) => {
  let attempts = 0;
  let consecutiveErrors = 0;

  while (attempts < maxAttempts) {
    if (shouldCancel()) {
      return { success: false, cancelled: true, error: "Processing cancelled" };
    }

    try {
      const status = await getProcessingStatus(requestId);
      consecutiveErrors = 0; // Reset on successful response

      if (shouldCancel()) {
        return {
          success: false,
          cancelled: true,
          error: "Processing cancelled",
        };
      }

      // Call the callback with current status
      onStatusUpdate(status);

      // Check if processing is complete
      if (status.status === "completed" || status.status === "COMPLETED") {
        return { success: true, status };
      }

      // Check if processing failed
      if (status.status === "failed" || status.status === "FAILED") {
        return {
          success: false,
          status,
          error: status.error_message || "Processing failed",
        };
      }

      // Wait before next poll
      await new Promise((resolve) => setTimeout(resolve, interval));
      attempts++;

      if (shouldCancel()) {
        return {
          success: false,
          cancelled: true,
          error: "Processing cancelled",
        };
      }
    } catch (error) {
      consecutiveErrors++;
      console.error(
        `Error polling status (${consecutiveErrors} consecutive):`,
        error,
      );

      // Fail fast if API is consistently unavailable
      if (consecutiveErrors >= 5) {
        return {
          success: false,
          error: `Polling aborted after ${consecutiveErrors} consecutive errors: ${error.message}`,
        };
      }

      attempts++;
      // Exponential backoff: double the wait per consecutive error, capped at 30s
      const backoffDelay = Math.min(
        interval * Math.pow(2, consecutiveErrors - 1),
        30000,
      );
      await new Promise((resolve) => setTimeout(resolve, backoffDelay));

      if (shouldCancel()) {
        return {
          success: false,
          cancelled: true,
          error: "Processing cancelled",
        };
      }
    }
  }

  return {
    success: false,
    error: "Processing timeout - exceeded maximum wait time",
  };
};

export const updateProcedure = async (partId, procedureIndex, procedure) => {
  const response = await fetch(
    getSopUrl(`/parts/${partId}/procedures/${procedureIndex}`),
    {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(procedure),
    },
  );

  return handleResponse(response, handleError);
};

export const deleteProcedure = async (partId, procedureIndex) => {
  const response = await fetch(
    getSopUrl(`/parts/${partId}/procedures/${procedureIndex}`),
    {
      method: "DELETE",
      headers: getAuthHeaders(),
    },
  );

  return handleResponse(response, handleError);
};

export const sopServices = {
  uploadDocument,
  getProcessingUploads,
  getParts,
  getPart,
  getProcessingStatus,
  getPartProcedures,
  getAssets,
  getOrgContainers,
  getContainerAssets,
  linkAssetsToPart,
  getLinkedAssets,
  unlinkAssetFromPart,
  updatePartMetadata,
  pollProcessingStatus,
  deleteProcessingUpload,
  deleteProcedure,
  approvePart,
};

export default sopServices;
